import { create } from 'zustand';
import { GameStateManager } from '@/core/GameStateManager';
import { MatchStateManager } from '@/core/MatchStateManager';
import { GameRules } from '@/core/GameRules';
import { Card } from '@/core/Card';
import { GameState, GamePhase, MatchState, RoundResult, SoloTeamChoice } from '@/types/game';
import { Player, Team } from '@/types/player';
import { CardColor, CardType } from '@/types/card';
import { ANIMATION_CONFIG } from '@/utils/constants';
import { useUIStore } from './uiStore';
import { useSettingsStore } from './settingsStore';
import type { GameStateUpdatePayload } from '@/types/network';

/**
 * アニメーション完了を待機するPromiseを作成
 * @param animationId アニメーションID
 * @param timeout タイムアウト（ms）
 * @returns アニメーション完了を待機するPromise
 */
const waitForAnimationComplete = (animationId: string, timeout: number = ANIMATION_CONFIG.ANIMATION_TIMEOUT): Promise<void> => {
  return new Promise((resolve) => {
    const uiStore = useUIStore.getState();

    // 即座にチェック
    const animation = uiStore.animationQueue.find(a => a.id === animationId);
    if (animation?.status === 'completed') {
      resolve();
      return;
    }

    // 購読して監視
    const unsubscribe = useUIStore.subscribe((state) => {
      const anim = state.animationQueue.find(a => a.id === animationId);
      if (anim?.status === 'completed' || !anim) {
        unsubscribe();
        resolve();
      }
    });

    // タイムアウト
    setTimeout(() => {
      unsubscribe();
      resolve();
    }, timeout);
  });
};

/**
 * ゲームモード
 */
type GameMode = 'solo' | 'local' | 'online';

/**
 * ゲームストア状態
 */
export interface GameStoreState {
  // コアゲーム状態
  gameManager: GameStateManager | null;
  gameState: GameState | null;

  // マッチ状態（持ち点システム）
  matchManager: MatchStateManager | null;
  matchState: MatchState | null;
  lastRoundResult: RoundResult | null;

  // ゲームモード
  mode: GameMode;
  localPlayerId: string | null;
  soloTeamChoice: SoloTeamChoice | null;

  // パフォーマンス最適化
  playableCardIds: Set<string>;

  // 状態管理
  isInitializing: boolean;
  error: string | null;

  // アクション: ゲーム管理
  initializeGame: (mode: GameMode, playerNames: string[], localPlayerId?: string, soloTeamChoice?: SoloTeamChoice) => void;
  resetGame: () => void;
  cleanup: () => void;

  // アクション: ゲームプレイ
  playCard: (playerId: string, cardId: string, declaredColor?: CardColor) => Promise<boolean>;
  drawCard: (playerId: string) => Promise<void>;
  passTurn: (playerId: string) => Promise<boolean>;

  // アクション: AI
  executeAITurn: () => Promise<void>;

  // アクション: ネットワーク同期
  initializeOnlineMode: (localPlayerId: string) => void;
  syncFromNetwork: (payload: GameStateUpdatePayload) => Promise<void>;

  // アクション: ローカルモード
  switchLocalPlayer: (playerId: string) => void;

  // アクション: マッチ管理
  startNextRound: () => void;
  getLifePoints: (playerId: string) => number;

  // ゲッター
  getCurrentPlayer: () => Player | null;
  getPlayerHand: (playerId: string) => Card[];
  getTeammateHands: (playerId: string) => Card[][];
  getTopCard: () => Card | null;
  canPlayCard: (playerId: string, cardId: string) => boolean;
  canViewHand: (viewerId: string, targetPlayerId: string) => boolean;
  updatePlayableCards: () => void;

  // アクション: 自動ペナルティドロー
  checkAutoPenaltyDraw: () => Promise<void>;
}

/**
 * AIが次にプレイするカードと色を選択
 * @param hand AIの手札
 * @param topCard 捨て札のトップカード
 * @param declaredColor 宣言されている色
 * @param drawPenaltyType 現在のペナルティ種別（あれば該当カードのみプレイ可能）
 * @param allowStacking スタッキングが許可されているか
 * @returns プレイするカードとオプションの宣言色
 */
const selectAICard = (
  hand: Card[],
  topCard: Card,
  declaredColor: CardColor | undefined,
  drawPenaltyType: import('@/types/game').DrawPenaltyType,
  allowStacking: boolean
): { card: Card; declaredColor?: CardColor } | null => {
  // ペナルティ状況を考慮してプレイ可能なカードをフィルタリング
  const playableCards = hand.filter(card =>
    GameRules.canPlayCardWithPenalty(card, topCard, declaredColor, drawPenaltyType, allowStacking)
  );

  if (playableCards.length === 0) {
    return null;
  }

  // 簡易AI: 最初のプレイ可能なカードを選択
  // TODO: Phase 8でより高度なAI戦略を実装
  const selectedCard = playableCards[0];

  // ワイルドカードの場合、色を選択
  let selectedColor: CardColor | undefined;
  if (GameRules.needsColorDeclaration(selectedCard)) {
    // 手札で最も多い色を選択
    selectedColor = selectBestColor(hand);
  }

  return { card: selectedCard, declaredColor: selectedColor };
};

/**
 * 手札で最も多い色を選択
 * @param hand 手札
 * @returns 最も多い色
 */
const selectBestColor = (hand: Card[]): CardColor => {
  const colorCounts: Record<CardColor, number> = {
    [CardColor.RED]: 0,
    [CardColor.BLUE]: 0,
    [CardColor.YELLOW]: 0,
    [CardColor.WILD]: 0
  };

  // 各色のカード枚数をカウント（ワイルドは除外）
  hand.forEach(card => {
    if (card.color !== CardColor.WILD) {
      colorCounts[card.color]++;
    }
  });

  // 最も多い色を選択
  let maxCount = 0;
  let bestColor: CardColor = CardColor.RED;

  Object.entries(colorCounts).forEach(([color, count]) => {
    if (color !== CardColor.WILD && count > maxCount) {
      maxCount = count;
      bestColor = color as CardColor;
    }
  });

  return bestColor;
};

/**
 * ゲームストア
 * ゲーム状態管理の中核、GameStateManagerをラップ
 */
export const useGameStore = create<GameStoreState>((set, get) => ({
  // 初期状態
  gameManager: null,
  gameState: null,
  matchManager: null,
  matchState: null,
  lastRoundResult: null,
  mode: 'solo',
  localPlayerId: null,
  soloTeamChoice: null,
  playableCardIds: new Set(),
  isInitializing: false,
  error: null,

  /**
   * ゲームを初期化
   * @param mode ゲームモード
   * @param playerNames プレイヤー名の配列
   * @param localPlayerId ローカルプレイヤーID（Local/Onlineモード用）
   * @param soloTeamChoice ソロモードのチーム選択（team1: デュオ側, team2: ソロ側）
   */
  initializeGame: (mode: GameMode, playerNames: string[], localPlayerId?: string, soloTeamChoice?: SoloTeamChoice) => {
    set({ isInitializing: true, error: null });

    try {
      // モード別のプレイヤー設定
      let finalPlayerNames = [...playerNames];
      let finalLocalPlayerId = localPlayerId;
      let finalSoloTeamChoice: SoloTeamChoice | null = null;

      if (mode === 'solo') {
        // Soloモード: プレイヤー1 + AI 2体
        if (playerNames.length !== 3) {
          throw new Error('Solo mode requires 3 player names');
        }
        finalLocalPlayerId = '1'; // ソロモードは常にプレイヤー1
        finalSoloTeamChoice = soloTeamChoice || 'team2'; // デフォルトはソロ側
      } else if (mode === 'local') {
        // Localモード: 3人プレイヤー
        if (playerNames.length !== 3) {
          throw new Error('Local mode requires 3 player names');
        }
        finalLocalPlayerId = localPlayerId || '1'; // デフォルトはプレイヤー1
      } else if (mode === 'online') {
        // Onlineモード: ネットワーク同期待機
        if (!localPlayerId) {
          throw new Error('Online mode requires localPlayerId');
        }
        finalLocalPlayerId = localPlayerId;
      }

      // GameStateManager初期化（ソロモードの場合はチーム選択を渡す）
      const gameManager = new GameStateManager(finalPlayerNames, finalSoloTeamChoice || undefined);

      // Soloモードの場合、AI フラグを設定
      if (mode === 'solo') {
        const state = gameManager.getState();
        state.players[1].isAI = true;
        state.players[2].isAI = true;
      }

      // MatchStateManager初期化（持ち点システム）
      const matchManager = new MatchStateManager();
      matchManager.initializeMatch(gameManager.getState().players);

      set({
        gameManager,
        gameState: gameManager.getState(),
        matchManager,
        matchState: matchManager.getState(),
        lastRoundResult: null,
        mode,
        localPlayerId: finalLocalPlayerId,
        soloTeamChoice: finalSoloTeamChoice,
        isInitializing: false
      });

      // プレイ可能カードを更新
      get().updatePlayableCards();

      console.log(`Game initialized in ${mode} mode`);
      console.log(`Local player ID: ${finalLocalPlayerId}`);

      // カード配布アニメーションをキューに追加
      const settings = useSettingsStore.getState();
      if (settings.visual.animationsEnabled) {
        useUIStore.getState().queueAnimation({
          type: 'deal_cards',
          data: {
            players: gameManager.getState().players,
            localPlayerId: finalLocalPlayerId || ''
          }
        });
      }

      // Soloモードで最初のプレイヤーがAIの場合、AI ターンを実行（アニメーション後）
      if (mode === 'solo') {
        const currentPlayer = gameManager.getCurrentPlayer();
        if (currentPlayer.isAI) {
          // カード配布アニメーションを待ってからAIターン実行
          const dealDelay = settings.visual.animationsEnabled
            ? gameManager.getState().players.length * 7 * ANIMATION_CONFIG.DEAL_STAGGER_DELAY + ANIMATION_CONFIG.CARD_DEAL_DURATION + ANIMATION_CONFIG.AI_TURN_DELAY
            : ANIMATION_CONFIG.AI_TURN_DELAY;
          setTimeout(() => {
            get().executeAITurn();
          }, dealDelay);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize game';
      set({
        error: errorMessage,
        isInitializing: false
      });
      console.error('Game initialization failed:', error);
    }
  },

  /**
   * ゲームをリセット
   */
  resetGame: () => {
    set({
      gameManager: null,
      gameState: null,
      matchManager: null,
      matchState: null,
      lastRoundResult: null,
      mode: 'solo',
      localPlayerId: null,
      soloTeamChoice: null,
      playableCardIds: new Set(),
      isInitializing: false,
      error: null
    });

    console.log('Game reset');
  },

  /**
   * クリーンアップ
   */
  cleanup: () => {
    get().resetGame();
    console.log('Game store cleaned up');
  },

  /**
   * カードをプレイ
   * @param playerId プレイヤーID
   * @param cardId カードID
   * @param declaredColor 宣言する色（ワイルドカードの場合）
   * @returns 成功したかどうか
   */
  playCard: async (playerId: string, cardId: string, declaredColor?: CardColor): Promise<boolean> => {
    const { mode, gameManager, localPlayerId } = get();

    if (!gameManager) {
      console.error('Game not initialized');
      return false;
    }

    // ゲーム終了チェック
    if (gameManager.getState().phase === GamePhase.FINISHED) {
      console.warn('Game is already finished');
      return false;
    }

    // モード別バリデーション
    if (mode === 'online' && playerId !== localPlayerId) {
      console.error('In online mode, only local player can play');
      return false;
    }

    // 現在のプレイヤーチェック
    if (gameManager.getCurrentPlayer().id !== playerId) {
      console.error('Not this player\'s turn');
      return false;
    }

    try {
      // アニメーションをキューに追加（アニメーション設定が有効な場合）
      const settings = useSettingsStore.getState();
      const animationsEnabled = settings.visual.animationsEnabled;

      if (animationsEnabled) {
        // アニメーションをキューに追加
        const animationId = useUIStore.getState().queueAnimation({
          type: 'play_card',
          data: { cardId, playerId, declaredColor }
        });

        // アニメーション完了を待機
        await waitForAnimationComplete(animationId);
      }

      // カードプレイ実行（ペナルティ状況を考慮）
      const allowStacking = useSettingsStore.getState().rules.allowDrawStacking;
      const success = gameManager.playCard(playerId, cardId, declaredColor, { allowStacking });

      if (success) {
        // 状態更新
        set({ gameState: gameManager.getState() });

        // プレイ可能カード更新
        get().updatePlayableCards();

        console.log(`Card played: ${cardId} by player ${playerId}`);

        // モード別の副作用
        if (mode === 'online') {
          // オンラインモード: サーバーからの状態更新を待つ（送信はUI層で実行済み）
        } else if (mode === 'solo') {
          // 自動ペナルティドローチェック（回避不可なら自動でドロー）
          await get().checkAutoPenaltyDraw();

          // ソロモード: 次のプレイヤーがAIならAI ターン実行
          const nextPlayer = gameManager.getCurrentPlayer();
          if (nextPlayer.isAI && gameManager.getState().phase === GamePhase.PLAYING) {
            setTimeout(() => {
              get().executeAITurn();
            }, ANIMATION_CONFIG.AI_TURN_DELAY);
          }
        } else if (mode === 'local') {
          // 自動ペナルティドローチェック（回避不可なら自動でドロー）
          await get().checkAutoPenaltyDraw();

          // ローカルモード: ターン遷移モーダルを表示
          const state = gameManager.getState();
          const nextPlayer = gameManager.getCurrentPlayer();

          // ゲーム継続中かつ次のプレイヤーに移行する場合
          if (state.phase === GamePhase.PLAYING && nextPlayer.id !== playerId) {
            setTimeout(() => {
              useUIStore.getState().openTurnTransition(nextPlayer.name, nextPlayer.id);
            }, ANIMATION_CONFIG.TURN_TRANSITION_DELAY);
          }
        }

        // ラウンド終了チェック（誰かが手札を0にした）
        const state = gameManager.getState();
        if (state.winner) {
          const { matchManager } = get();

          if (matchManager) {
            // ラウンド結果を計算してマッチ状態を更新
            const roundResult = matchManager.applyRoundResult(state.winner, state.players);
            const matchState = matchManager.getState();

            set({
              matchState,
              lastRoundResult: roundResult
            });

            console.log(`Round ${roundResult.roundNumber} finished! Winner: ${state.winner}`);
            console.log('Damage dealt:', roundResult.damageDealt);

            // ターン遷移モーダルが開いている場合は閉じる
            if (useUIStore.getState().modals.turnTransition.isOpen) {
              useUIStore.getState().closeTurnTransition();
            }

            // 常にラウンド結果モーダルを表示
            // 試合終了判定は RoundResultModal 側で「次のラウンドへ」押下時に実施し、
            // 終了時のみ closeRoundResult → openGameOver へ遷移する
            useUIStore.getState().openRoundResult(roundResult);

            // マッチ終了の場合は事前に統計記録だけ済ませておく
            // （UI遷移は RoundResultModal 側で実施）
            if (matchManager.isMatchFinished()) {
              const matchWinner = matchManager.getMatchWinner();
              console.log(`Match over! Winner: ${matchWinner}`);

              // 統計記録（ローカルプレイヤーが勝利チームかどうか）
              const localPlayer = state.players.find(p => p.id === localPlayerId);
              const isWin = localPlayer?.team === matchWinner;
              useSettingsStore.getState().recordGamePlayed(isWin);
            }
          }
        }
      } else {
        console.error('Failed to play card');
      }

      return success;
    } catch (error) {
      console.error('Error playing card:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to play card' });
      return false;
    }
  },

  /**
   * カードをドロー
   * @param playerId プレイヤーID
   */
  drawCard: async (playerId: string): Promise<void> => {
    const { mode, gameManager, localPlayerId } = get();

    if (!gameManager) {
      console.error('Game not initialized');
      return;
    }

    // ゲーム終了チェック
    if (gameManager.getState().phase === GamePhase.FINISHED) {
      console.warn('Game is already finished');
      return;
    }

    // モード別バリデーション
    if (mode === 'online' && playerId !== localPlayerId) {
      console.error('In online mode, only local player can draw');
      return;
    }

    // 現在のプレイヤーチェック
    if (gameManager.getCurrentPlayer().id !== playerId) {
      console.error('Not this player\'s turn');
      return;
    }

    try {
      // ペナルティドロー（drawCount > 0）かどうかを判定
      // ペナルティドロー時はそのままターン進行、任意ドローはターンを進めず判断機会を残す
      const stateBeforeDraw = gameManager.getState();
      const isPenaltyDraw = stateBeforeDraw.drawCount > 0;

      // カードドロー実行（任意ドローはターンを進めない）
      gameManager.drawCard(playerId, { advanceTurn: isPenaltyDraw });

      // ドローしたカードを取得（手札の最後のカード）
      const player = gameManager.getState().players.find(p => p.id === playerId);
      const drawnCard = player?.hand[player.hand.length - 1];

      // アニメーションをキューに追加（アニメーション設定が有効な場合）
      const settings = useSettingsStore.getState();
      const animationsEnabled = settings.visual.animationsEnabled;

      if (animationsEnabled && drawnCard) {
        // アニメーションをキューに追加
        const animationId = useUIStore.getState().queueAnimation({
          type: 'draw_card',
          data: { playerId, card: drawnCard }
        });

        // アニメーション完了を待機
        await waitForAnimationComplete(animationId);
      }

      // 任意ドロー後にプレイ可能なカードがなければ自動でパス
      // （ペナルティドロー時はターンが既に進んでいるのでスキップ）
      if (!isPenaltyDraw) {
        const stateAfterDraw = gameManager.getState();
        const handAfterDraw = stateAfterDraw.players.find(p => p.id === playerId)?.hand || [];
        const topCard = gameManager.getTopCard();
        const declaredColor = stateAfterDraw.declaredColor;
        const allowStacking = useSettingsStore.getState().rules.allowDrawStacking;

        const hasPlayable = handAfterDraw.some(card =>
          GameRules.canPlayCardWithPenalty(card, topCard, declaredColor, stateAfterDraw.drawPenaltyType, allowStacking)
        );

        if (!hasPlayable) {
          // プレイ可能カードがない → 自動でパス（ターン進行）
          gameManager.passTurn(playerId);
        }
      }

      // 状態更新
      set({ gameState: gameManager.getState() });

      // プレイ可能カード更新
      get().updatePlayableCards();

      console.log(`Card drawn by player ${playerId}`);

      // ターンが進んだかを判定（hasDrawnThisTurnで判別）
      const stateAfter = gameManager.getState();
      const turnAdvanced = !stateAfter.hasDrawnThisTurn;

      // モード別の副作用
      if (mode === 'online') {
        // オンラインモード: サーバーからの状態更新を待つ（送信はUI層で実行済み）
      } else if (mode === 'solo') {
        // ソロモード: ターンが進み、次のプレイヤーがAIならAI ターン実行
        if (turnAdvanced) {
          const nextPlayer = gameManager.getCurrentPlayer();
          if (nextPlayer.isAI && stateAfter.phase === GamePhase.PLAYING) {
            setTimeout(() => {
              get().executeAITurn();
            }, ANIMATION_CONFIG.AI_TURN_DELAY);
          }
        }
      } else if (mode === 'local') {
        // ローカルモード: ターンが進んだ場合はターン遷移モーダルを表示
        if (turnAdvanced) {
          const nextPlayer = gameManager.getCurrentPlayer();
          if (stateAfter.phase === GamePhase.PLAYING && nextPlayer.id !== playerId) {
            setTimeout(() => {
              useUIStore.getState().openTurnTransition(nextPlayer.name, nextPlayer.id);
            }, ANIMATION_CONFIG.TURN_TRANSITION_DELAY);
          }
        }
      }
    } catch (error) {
      console.error('Error drawing card:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to draw card' });
    }
  },

  /**
   * ターンをパス（任意ドロー後にカードをプレイしない選択）
   * @param playerId プレイヤーID
   * @returns 成功したかどうか
   */
  passTurn: async (playerId: string): Promise<boolean> => {
    const { mode, gameManager, localPlayerId } = get();

    if (!gameManager) {
      console.error('Game not initialized');
      return false;
    }

    if (gameManager.getState().phase === GamePhase.FINISHED) {
      return false;
    }

    if (mode === 'online' && playerId !== localPlayerId) {
      console.error('In online mode, only local player can pass');
      return false;
    }

    const success = gameManager.passTurn(playerId);
    if (!success) {
      console.warn(`Pass turn failed for ${playerId}`);
      return false;
    }

    // 状態更新
    set({ gameState: gameManager.getState() });
    get().updatePlayableCards();

    // モード別の副作用（ターン進行に伴う処理）
    if (mode === 'solo') {
      const nextPlayer = gameManager.getCurrentPlayer();
      if (nextPlayer.isAI && gameManager.getState().phase === GamePhase.PLAYING) {
        setTimeout(() => {
          get().executeAITurn();
        }, ANIMATION_CONFIG.AI_TURN_DELAY);
      }
    } else if (mode === 'local') {
      const state = gameManager.getState();
      const nextPlayer = gameManager.getCurrentPlayer();
      if (state.phase === GamePhase.PLAYING && nextPlayer.id !== playerId) {
        setTimeout(() => {
          useUIStore.getState().openTurnTransition(nextPlayer.name, nextPlayer.id);
        }, ANIMATION_CONFIG.TURN_TRANSITION_DELAY);
      }
    }

    return true;
  },

  /**
   * AIターンを実行（ソロモード専用）
   */
  executeAITurn: async (): Promise<void> => {
    const { mode, gameManager } = get();

    if (mode !== 'solo') {
      console.warn('AI turn execution is only for solo mode');
      return;
    }

    if (!gameManager) {
      console.error('Game not initialized');
      return;
    }

    const currentPlayer = gameManager.getCurrentPlayer();
    if (!currentPlayer.isAI) {
      console.warn('Current player is not AI');
      return;
    }

    if (gameManager.getState().phase !== GamePhase.PLAYING) {
      console.warn('Game is not in playing phase');
      return;
    }

    try {
      console.log(`AI turn: Player ${currentPlayer.id} (${currentPlayer.name})`);

      // AI思考時間（ランダム）
      const thinkTime = Math.random() * (ANIMATION_CONFIG.AI_THINK_MAX - ANIMATION_CONFIG.AI_THINK_MIN) + ANIMATION_CONFIG.AI_THINK_MIN;
      await new Promise(resolve => setTimeout(resolve, thinkTime));

      const state = gameManager.getState();
      const topCard = gameManager.getTopCard();
      const declaredColor = state.declaredColor;
      const drawPenaltyType = state.drawPenaltyType;
      const allowStacking = useSettingsStore.getState().rules.allowDrawStacking;

      // AIカード選択（ペナルティ状況を考慮）
      const aiDecision = selectAICard(
        currentPlayer.hand,
        topCard,
        declaredColor,
        drawPenaltyType,
        allowStacking
      );

      if (aiDecision) {
        // カードをプレイ
        console.log(`AI plays card: ${aiDecision.card.toString()}`);
        await get().playCard(currentPlayer.id, aiDecision.card.id, aiDecision.declaredColor);
      } else {
        // プレイできるカードがない場合、ドロー
        console.log('AI draws card');
        await get().drawCard(currentPlayer.id);

        // 任意ドロー後にターンが残っているかチェック
        // （プレイ可能カードがあった場合はパスせずに残るため、AIが次の判断をする必要がある）
        const stateAfterDraw = gameManager.getState();
        if (
          stateAfterDraw.phase === GamePhase.PLAYING &&
          gameManager.getCurrentPlayer().id === currentPlayer.id
        ) {
          // ドロー後もターンが残っている場合、AIはドローしたカード（または他のカード）をプレイするか判断
          const aiNextDecision = selectAICard(
            gameManager.getCurrentPlayer().hand,
            gameManager.getTopCard(),
            stateAfterDraw.declaredColor,
            stateAfterDraw.drawPenaltyType,
            allowStacking
          );

          if (aiNextDecision) {
            // ドロー後にプレイ可能なカードがあればプレイ
            console.log(`AI plays drawn card: ${aiNextDecision.card.toString()}`);
            await get().playCard(currentPlayer.id, aiNextDecision.card.id, aiNextDecision.declaredColor);
          } else {
            // それでもプレイ可能カードがない場合はパス（基本的に到達しないが安全策）
            console.log('AI passes after draw');
            await get().passTurn(currentPlayer.id);
          }
        }
      }
    } catch (error) {
      console.error('Error executing AI turn:', error);
      set({ error: error instanceof Error ? error.message : 'AI turn failed' });
    }
  },

  /**
   * オンラインモードを初期化（非ホストプレイヤー用）
   * @param localPlayerId ローカルプレイヤーID
   */
  initializeOnlineMode: (localPlayerId: string) => {
    console.log('Game initialized in online mode');
    console.log('Local player ID:', localPlayerId);
    set({
      mode: 'online',
      localPlayerId,
      isInitializing: false
    });
  },

  /**
   * ネットワークから状態を同期（オンラインモード専用）
   * @param payload サーバーからのゲーム状態ペイロード
   */
  syncFromNetwork: async (payload: GameStateUpdatePayload) => {
    const { mode, localPlayerId, matchManager, gameState: prevGameState } = get();

    if (mode !== 'online') {
      console.warn('Network sync is only for online mode');
      return;
    }

    try {
      // サーバーのカードオブジェクトをCardインスタンスに変換
      const convertToCard = (cardData: any): Card => {
        return new Card(
          cardData.id,
          cardData.color as CardColor,
          cardData.type as CardType,
          cardData.value
        );
      };

      // 自分の手札をCardインスタンスに変換
      const myHand = payload.myHand.map(convertToCard);

      // チームメイトの手札をCardインスタンスに変換
      const teammateHands = payload.teammateHands.map(hand =>
        hand.map(convertToCard)
      );

      // トップカードをCardインスタンスに変換
      const topCard = convertToCard(payload.gameState.topCard);

      // プレイヤー情報を構築
      const playerIds = Object.keys(payload.playerNames);
      const players: Player[] = playerIds.map(playerId => {
        const isMe = playerId === localPlayerId;
        const isTeammate = payload.playerTeams[playerId] === payload.playerTeams[localPlayerId || ''];

        // 手札を決定
        let hand: Card[];
        if (isMe) {
          hand = myHand;
        } else if (isTeammate && teammateHands.length > 0) {
          // チームメイトの手札（自分以外の同チーム）
          const teammateIndex = playerIds
            .filter(id => id !== localPlayerId && payload.playerTeams[id] === payload.playerTeams[localPlayerId || ''])
            .indexOf(playerId);
          hand = teammateHands[teammateIndex] || [];
        } else {
          // 対戦相手の手札は空（枚数のみ表示）
          // 枚数分の空のカードを作成（UIでカード裏面として表示）
          const cardCount = payload.opponentHandCounts[playerId] || 0;
          hand = Array.from({ length: cardCount }, (_, i) =>
            new Card(`hidden_${playerId}_${i}`, CardColor.WILD, CardType.WILD, undefined)
          );
        }

        return {
          id: playerId,
          name: payload.playerNames[playerId],
          hand,
          team: payload.playerTeams[playerId] as Team,
          isAI: false,
          isConnected: true,
          unoDeclaration: payload.unoDeclarations[playerId] || false
        };
      });

      // GamePhaseをマッピング
      const phaseMap: Record<string, GamePhase> = {
        'waiting': GamePhase.WAITING,
        'playing': GamePhase.PLAYING,
        'finished': GamePhase.FINISHED
      };

      // GameStateを構築
      // サーバーはデッキの内容を送らず枚数のみ送るため、ダミーのデッキを作成
      const dummyDeck: Card[] = Array.from({ length: payload.gameState.deckCount }, (_, i) =>
        new Card(`deck_${i}`, CardColor.WILD, CardType.WILD, undefined)
      );

      // 2v1 変則ターン順 (issue #47/#48/#49) 用の派生フィールド。
      // サーバーは crookIndex/copIndices/lastCopIndex/alternationFlipped を
      // まだ送信しないため、client 側で players の team から再構築する。
      // サーバー側未対応の間は alternationFlipped=false 固定 (Reverse の挙動は
      // online mode では旧 direction 反転にフォールバックする — 別 issue で対応)。
      const team1Indices: number[] = [];
      const team2Indices: number[] = [];
      players.forEach((p, idx) => {
        if (p.team === Team.TEAM_1) team1Indices.push(idx);
        else team2Indices.push(idx);
      });
      const isTeam1Crook = team1Indices.length === 1;
      const crookIndex = isTeam1Crook ? team1Indices[0] : team2Indices[0];
      const copIndices = isTeam1Crook ? team2Indices : team1Indices;

      const gameState: GameState = {
        phase: phaseMap[payload.gameState.phase] || GamePhase.PLAYING,
        players,
        currentPlayerIndex: payload.gameState.currentPlayerIndex,
        direction: payload.gameState.direction,
        deck: dummyDeck,
        discardPile: [topCard],  // サーバーはトップカードのみ送信
        drawCount: payload.gameState.drawCount,
        drawPenaltyType: payload.gameState.drawPenaltyType ?? null,
        declaredColor: payload.gameState.declaredColor,
        winner: payload.gameState.winner,
        crookIndex,
        copIndices,
        lastCopIndex: copIndices[0] ?? 0,
        alternationFlipped: false
      };

      // MatchStateを更新（ライフポイント）
      if (matchManager) {
        // ライフポイントを同期
        Object.entries(payload.lifePoints).forEach(([playerId, points]) => {
          matchManager.setLifePoints(playerId, points);
        });
      }

      // ペナルティドロー検出: 前の状態がペナルティ中（drawCount > 0）で、
      // 今回の状態でリセットされている場合、手札が増えたプレイヤーがペナルティを受けた可能性が高い。
      // そのプレイヤーへ1枚ずつ draw_card アニメーションをキューする。
      const settings = useSettingsStore.getState();
      const wasPenaltyActive = !!prevGameState && prevGameState.drawCount > 0;
      const isPenaltyResolved = gameState.drawCount === 0 && payload.gameState.drawPenaltyType == null;

      if (
        settings.visual.animationsEnabled &&
        wasPenaltyActive &&
        isPenaltyResolved &&
        prevGameState
      ) {
        // ペナルティ枚数（スタッキングを含む。例: Draw2+Draw2=4、Draw4+Draw4=8）
        const expectedPenaltyCount = prevGameState.drawCount;

        // 手札枚数が増えたプレイヤーを特定（ペナルティ枚数分増えたプレイヤーが対象）
        for (const newPlayer of players) {
          const prevPlayer = prevGameState.players.find(p => p.id === newPlayer.id);
          if (!prevPlayer) continue;
          const diff = newPlayer.hand.length - prevPlayer.hand.length;

          if (diff === expectedPenaltyCount && diff > 0) {
            // 末尾 diff 枚を取り出し、1枚ずつアニメーション再生
            // ※ 自分以外のプレイヤーは hidden_xxx カードのため裏面で表示される
            const drawnCards = newPlayer.hand.slice(newPlayer.hand.length - diff);

            // drawCardAnimation を直接キューして最後の set でまとめて反映する。
            for (let i = 0; i < drawnCards.length; i++) {
              const card = drawnCards[i];
              const animationId = useUIStore.getState().queueAnimation({
                type: 'draw_card',
                data: { playerId: newPlayer.id, card }
              });
              await waitForAnimationComplete(animationId);

              if (i < drawnCards.length - 1) {
                await new Promise(resolve =>
                  setTimeout(resolve, ANIMATION_CONFIG.PENALTY_DRAW_STAGGER_DELAY)
                );
              }
            }
            // ペナルティドロー対象は1人なので、見つけたら終了
            break;
          }
        }
      }

      // 状態を更新
      set({
        gameState,
        matchState: matchManager?.getState() || null
      });

      // プレイ可能カード更新
      get().updatePlayableCards();

      console.log(`Game state synced from network. Phase: ${payload.gameState.phase}, Current player: ${payload.gameState.currentPlayerId}`);
    } catch (error) {
      console.error('Error syncing from network:', error);
      set({ error: error instanceof Error ? error.message : 'Network sync failed' });
    }
  },

  /**
   * ローカルプレイヤーを切り替え（ローカルモード専用）
   * @param playerId 新しいローカルプレイヤーID
   */
  switchLocalPlayer: (playerId: string) => {
    const { mode, gameManager } = get();

    if (mode !== 'local') {
      console.warn('Player switching is only for local mode');
      return;
    }

    if (!gameManager) {
      console.error('Game not initialized');
      return;
    }

    const player = gameManager.getState().players.find(p => p.id === playerId);
    if (!player) {
      console.error(`Player ${playerId} not found`);
      return;
    }

    // gameStateの新しい参照を作成してPhaserの再同期をトリガー
    const currentGameState = get().gameState;
    set({
      localPlayerId: playerId,
      gameState: currentGameState ? { ...currentGameState } : null
    });

    // プレイ可能カード更新
    get().updatePlayableCards();

    console.log(`Switched to local player: ${playerId} (${player.name})`);
  },

  /**
   * 現在のプレイヤーを取得
   * @returns 現在のプレイヤー
   */
  getCurrentPlayer: () => {
    const { gameManager } = get();
    return gameManager?.getCurrentPlayer() || null;
  },

  /**
   * プレイヤーの手札を取得
   * @param playerId プレイヤーID
   * @returns プレイヤーの手札
   */
  getPlayerHand: (playerId: string) => {
    const { gameManager } = get();
    return gameManager?.getPlayerHand(playerId) || [];
  },

  /**
   * チームメイトの手札を取得
   * @param playerId プレイヤーID
   * @returns チームメイトの手札配列
   */
  getTeammateHands: (playerId: string) => {
    const { gameManager } = get();
    return gameManager?.getTeammateHands(playerId) || [];
  },

  /**
   * 捨て札のトップカードを取得
   * @returns トップカード
   */
  getTopCard: () => {
    const { gameManager } = get();
    return gameManager?.getTopCard() || null;
  },

  /**
   * カードがプレイ可能か判定
   * @param playerId プレイヤーID
   * @param cardId カードID
   * @returns プレイ可能かどうか
   */
  canPlayCard: (playerId: string, cardId: string) => {
    const { gameManager } = get();

    if (!gameManager) {
      return false;
    }

    const state = gameManager.getState();
    const player = state.players.find(p => p.id === playerId);
    if (!player) {
      return false;
    }

    const card = player.hand.find(c => c.id === cardId);
    if (!card) {
      return false;
    }

    const topCard = gameManager.getTopCard();
    const declaredColor = state.declaredColor;
    const drawPenaltyType = state.drawPenaltyType;
    const allowStacking = useSettingsStore.getState().rules.allowDrawStacking;

    return GameRules.canPlayCardWithPenalty(card, topCard, declaredColor, drawPenaltyType, allowStacking);
  },

  /**
   * 手札を閲覧可能か判定
   * @param viewerId 閲覧者のプレイヤーID
   * @param targetPlayerId 対象プレイヤーID
   * @returns 閲覧可能かどうか
   */
  canViewHand: (viewerId: string, targetPlayerId: string): boolean => {
    const { mode, gameManager } = get();

    // 自分の手札は常に見える
    if (viewerId === targetPlayerId) {
      return true;
    }

    if (!gameManager) {
      return false;
    }

    const players = gameManager.getState().players;
    const viewer = players.find(p => p.id === viewerId);
    const target = players.find(p => p.id === targetPlayerId);

    if (!viewer || !target) {
      return false;
    }

    // ソロモード: 自分の手札のみ閲覧可
    // (issue #54: Duo 戦の CPU2 味方の手札も伏せ札表示にする)
    if (mode === 'solo') {
      return false;
    }

    // オンラインモードでは自分の手札のみ
    if (mode === 'online') {
      return false;
    }

    // ローカルモード: チームメイトの手札は見える
    return viewer.team === target.team;
  },

  /**
   * プレイ可能カードを更新
   * gameManagerがない場合（オンラインモード）でもgameStateから直接計算
   */
  updatePlayableCards: () => {
    const { gameManager, gameState, localPlayerId, mode } = get();
    const allowStacking = useSettingsStore.getState().rules.allowDrawStacking;

    if (!localPlayerId) {
      set({ playableCardIds: new Set() });
      return;
    }

    // オンラインモード: gameStateから直接計算
    if (mode === 'online' && gameState) {
      const player = gameState.players.find(p => p.id === localPlayerId);
      if (!player) {
        set({ playableCardIds: new Set() });
        return;
      }

      // 自分のターンでない場合は空
      const currentPlayer = gameState.players[gameState.currentPlayerIndex];
      if (!currentPlayer || currentPlayer.id !== localPlayerId) {
        set({ playableCardIds: new Set() });
        console.log(`Not my turn - clearing playable cards (current: ${currentPlayer?.id}, me: ${localPlayerId})`);
        return;
      }

      const topCard = gameState.discardPile[gameState.discardPile.length - 1];
      if (!topCard) {
        set({ playableCardIds: new Set() });
        return;
      }

      const declaredColor = gameState.declaredColor;
      const drawPenaltyType = gameState.drawPenaltyType;

      const playable = new Set(
        player.hand
          .filter(card => GameRules.canPlayCardWithPenalty(card, topCard, declaredColor, drawPenaltyType, allowStacking))
          .map(card => card.id)
      );

      set({ playableCardIds: playable });
      console.log(`Updated playable cards (online): ${playable.size} cards`);
      return;
    }

    // ローカル/ソロモード: gameManagerを使用
    if (!gameManager) {
      set({ playableCardIds: new Set() });
      return;
    }

    const state = gameManager.getState();
    const player = state.players.find(p => p.id === localPlayerId);
    if (!player) {
      set({ playableCardIds: new Set() });
      return;
    }

    const topCard = gameManager.getTopCard();
    const declaredColor = state.declaredColor;
    const drawPenaltyType = state.drawPenaltyType;

    const playable = new Set(
      player.hand
        .filter(card => GameRules.canPlayCardWithPenalty(card, topCard, declaredColor, drawPenaltyType, allowStacking))
        .map(card => card.id)
    );

    set({ playableCardIds: playable });

    console.log(`Updated playable cards: ${playable.size} cards`);
  },

  /**
   * 自動ペナルティドローチェック
   * Draw2/Draw4のペナルティを回避できない場合、自動的にドローを実行
   */
  checkAutoPenaltyDraw: async () => {
    const { gameManager, mode } = get();

    // オンラインモードはサーバーが処理するのでスキップ
    if (mode === 'online') return;

    if (!gameManager) return;

    const state = gameManager.getState();
    if (state.drawCount === 0 || state.phase === GamePhase.FINISHED) return;

    const allowStacking = useSettingsStore.getState().rules.allowDrawStacking;
    const currentPlayer = gameManager.getCurrentPlayer();

    // ペナルティを回避できるかチェック
    if (GameRules.canAvoidDrawPenalty(
      currentPlayer.hand,
      state.drawPenaltyType,
      allowStacking
    )) {
      // 回避可能：プレイヤーの選択を待つ
      console.log(`Player ${currentPlayer.name} can avoid penalty with stacking`);
      return;
    }

    // 回避不可：自動でドロー
    console.log(`Auto penalty draw: ${state.drawCount} cards for ${currentPlayer.name}`);

    // ドロー前の手札枚数を記録（アニメーション用に引いたカードを後で抽出）
    const prevHandLength = currentPlayer.hand.length;

    // 自動ドロー実行（手札・デッキは即座に更新される）
    gameManager.processAutoPenaltyDraw(allowStacking);

    // 引いたカードを取得（手札の末尾に追加されている）
    const updatedPlayer = gameManager.getState().players.find(p => p.id === currentPlayer.id);
    const drawnCards = updatedPlayer ? updatedPlayer.hand.slice(prevHandLength) : [];

    // アニメーションを1枚ずつ順次再生
    // ※ set({ gameState }) はループ後にまとめて呼ぶ。
    //   Phaser側のスプライトは drawCardAnimation 内で個別に追加される。
    //   最終 set 時の syncWithGameState では diff=0 となり追加描画は走らない。
    const settings = useSettingsStore.getState();
    if (settings.visual.animationsEnabled && drawnCards.length > 0) {
      for (let i = 0; i < drawnCards.length; i++) {
        const card = drawnCards[i];
        const animationId = useUIStore.getState().queueAnimation({
          type: 'draw_card',
          data: { playerId: currentPlayer.id, card }
        });
        await waitForAnimationComplete(animationId);

        // 最後のカード以外は短いインターバルを挟む（1枚ずつ増えている感を強調）
        if (i < drawnCards.length - 1) {
          await new Promise(resolve =>
            setTimeout(resolve, ANIMATION_CONFIG.PENALTY_DRAW_STAGGER_DELAY)
          );
        }
      }
    }

    // 状態更新（アニメーション完了後に1回だけ）
    set({ gameState: gameManager.getState() });
    get().updatePlayableCards();

    // 自動ドロー後、次のプレイヤーがAIならAIターンを実行
    if (mode === 'solo') {
      const nextPlayer = gameManager.getCurrentPlayer();
      if (nextPlayer.isAI && gameManager.getState().phase === GamePhase.PLAYING) {
        setTimeout(() => {
          get().executeAITurn();
        }, ANIMATION_CONFIG.AI_TURN_DELAY);
      }
    } else if (mode === 'local') {
      // ローカルモード: ターン遷移モーダルを表示
      const state2 = gameManager.getState();
      const nextPlayer = gameManager.getCurrentPlayer();
      if (state2.phase === GamePhase.PLAYING && nextPlayer.id !== currentPlayer.id) {
        setTimeout(() => {
          useUIStore.getState().openTurnTransition(nextPlayer.name, nextPlayer.id);
        }, ANIMATION_CONFIG.TURN_TRANSITION_DELAY);
      }
    }
  },

  /**
   * 次のラウンドを開始
   */
  startNextRound: () => {
    const { mode, matchManager, gameState, soloTeamChoice } = get();

    if (!matchManager || !gameState) {
      console.error('Match or game not initialized');
      return;
    }

    if (matchManager.isMatchFinished()) {
      console.warn('Match is already finished');
      return;
    }

    // マッチ状態を次のラウンドに進める
    matchManager.startNextRound();

    // プレイヤー名を取得
    const playerNames = gameState.players.map(p => p.name);

    // 新しいGameStateManagerを作成（ソロモードの場合はチーム選択を維持）
    const gameManager = new GameStateManager(playerNames, soloTeamChoice || undefined);

    // Soloモードの場合、AI フラグを設定
    if (mode === 'solo') {
      const state = gameManager.getState();
      state.players[1].isAI = true;
      state.players[2].isAI = true;
    }

    const localPlayerId = get().localPlayerId;

    set({
      gameManager,
      gameState: gameManager.getState(),
      matchState: matchManager.getState(),
      lastRoundResult: null
    });

    // プレイ可能カードを更新
    get().updatePlayableCards();

    console.log(`Round ${matchManager.getRoundNumber()} started`);

    // カード配布アニメーションをキューに追加
    const settings = useSettingsStore.getState();
    if (settings.visual.animationsEnabled) {
      useUIStore.getState().queueAnimation({
        type: 'deal_cards',
        data: {
          players: gameManager.getState().players,
          localPlayerId: localPlayerId || ''
        }
      });
    }

    // Soloモードで最初のプレイヤーがAIの場合、AI ターンを実行（アニメーション後）
    if (mode === 'solo') {
      const currentPlayer = gameManager.getCurrentPlayer();
      if (currentPlayer.isAI) {
        // カード配布アニメーションを待ってからAIターン実行
        const dealDelay = settings.visual.animationsEnabled
          ? gameManager.getState().players.length * 7 * ANIMATION_CONFIG.DEAL_STAGGER_DELAY + ANIMATION_CONFIG.CARD_DEAL_DURATION + ANIMATION_CONFIG.AI_TURN_DELAY
          : ANIMATION_CONFIG.AI_TURN_DELAY;
        setTimeout(() => {
          get().executeAITurn();
        }, dealDelay);
      }
    }
  },

  /**
   * プレイヤーの持ち点を取得
   * @param playerId プレイヤーID
   * @returns 持ち点
   */
  getLifePoints: (playerId: string): number => {
    const { matchManager } = get();
    return matchManager?.getLifePoints(playerId) ?? 0;
  }
}));

// 型エクスポート
export type { GameMode };
